import React, { useEffect, useState, useRef, useMemo } from 'react';
import EmployeeEngagement from './EmployeeEngagement';
import EmployeeSatisfaction from './EmployeeSatisfaction';
import EmployeeWellbeing from './EmployeeWellbeing';
import ContentEngagement from './ContentEngagement';
import { reportService } from '../../services';
import { processWellbeingData } from '../../utils/wellbeingDataProcessor';
import type { DauReportsResponse, FeedbacksReportsResponse, ProcessedWellbeingData } from '../../types/reports';

interface KPIDashboardProps {
  dauData: DauReportsResponse | null;
  feedbackData: FeedbacksReportsResponse | null;
  wellbeingData: ProcessedWellbeingData | null;
  compareToRange?: {
    startDay: string;
    endDay: string;
  };
  loading?: {
    dau?: boolean;
    feedback?: boolean;
    wellbeing?: boolean;
  };
}

const KPIDashboard: React.FC<KPIDashboardProps> = ({
  dauData,
  feedbackData,
  wellbeingData,
  compareToRange,
  loading = {},
}) => {
  // State for comparison data
  const [comparisonDauData, setComparisonDauData] = useState<DauReportsResponse | null>(null);
  const [comparisonFeedbackData, setComparisonFeedbackData] = useState<FeedbacksReportsResponse | null>(null);
  const [comparisonWellbeingData, setComparisonWellbeingData] = useState<ProcessedWellbeingData | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);

  // Use refs to track if we're already loading to prevent multiple calls
  const isLoadingRef = useRef(false);
  const lastCompareRangeRef = useRef<string>('');
  const primaryDataLoadedRef = useRef(false);

  // Create stable boolean flags for data existence to avoid re-running effect on data changes
  const hasDauData = useMemo(() => !!dauData, [dauData]);
  const hasFeedbackData = useMemo(() => !!feedbackData, [feedbackData]);
  const hasWellbeingData = useMemo(() => !!wellbeingData, [wellbeingData]);

  // Track when primary data transitions from empty to loaded
  useEffect(() => {
    const hasAnyPrimaryData = dauData || feedbackData || wellbeingData;
    const isStillLoadingPrimary = loading?.dau || loading?.feedback || loading?.wellbeing;
    
    // If we now have primary data and we're not loading anymore
    if (hasAnyPrimaryData && !isStillLoadingPrimary && !primaryDataLoadedRef.current) {
      primaryDataLoadedRef.current = true;
      // Reset the last loaded range so comparison data can be fetched
      lastCompareRangeRef.current = '';
    }
    
    // Reset when all data is cleared
    if (!hasAnyPrimaryData && !isStillLoadingPrimary) {
      primaryDataLoadedRef.current = false;
    }
  }, [dauData, feedbackData, wellbeingData, loading?.dau, loading?.feedback, loading?.wellbeing]);

  // Load comparison data when compareToRange changes
  useEffect(() => {
    let isMounted = true;
    
    const loadComparisonData = async () => {
      // Create a unique key for the compare range to prevent duplicate calls
      const rangeKey = compareToRange ? `${compareToRange.startDay}-${compareToRange.endDay}` : '';
      
      // If already loading, return
      if (isLoadingRef.current) {
        return;
      }
      
      if (!compareToRange) {
        if (isMounted) {
          setComparisonDauData(null);
          setComparisonFeedbackData(null);
          setComparisonWellbeingData(null);
        }
        lastCompareRangeRef.current = '';
        return;
      }

      // If we already loaded this exact range, skip refetch
      if (lastCompareRangeRef.current === rangeKey) {
        return;
      }

      // Wait until primary data stops loading before fetching comparison
      const isStillLoadingPrimary = loading?.dau || loading?.feedback || loading?.wellbeing;
      
      if (isStillLoadingPrimary) {
        return;
      }
      
      // Also ensure we have at least some primary data available
      const hasAnyPrimaryData = dauData || feedbackData || wellbeingData;
      if (!hasAnyPrimaryData) {
        return;
      }
      
      // Mark as loading to prevent concurrent fetches
      isLoadingRef.current = true;
      
      if (isMounted) {
        setLoadingComparison(true);
      }
      
      try {
        // Fetch DAU comparison data directly using the service to avoid hook state conflicts
        if (dauData && isMounted) {
          try {
            const comparisonDauResult = await reportService.getDauReports({
              startDate: compareToRange.startDay,
              endDate: compareToRange.endDay
            });
            if (isMounted) {
              setComparisonDauData(comparisonDauResult);
            }
          } catch (error) {
            console.error('[KPIDashboard] Failed to fetch DAU comparison data:', error);
            if (isMounted) {
              setComparisonDauData(null);
            }
          }
        }

        // Fetch Feedback comparison data
        if (feedbackData && isMounted) {
          try {
            const comparisonFeedbackResult = await reportService.getFeedbackReports({
              startDay: compareToRange.startDay,
              endDay: compareToRange.endDay,
              includeAllVersions: true
            });
            if (isMounted) {
              setComparisonFeedbackData(comparisonFeedbackResult);
            }
          } catch (error) {
            console.error('[KPIDashboard] Failed to fetch feedback comparison data:', error);
            if (isMounted) {
              setComparisonFeedbackData(null);
            }
          }
        }

        // Fetch Wellbeing comparison data
        if (wellbeingData && isMounted) {
          try {
            const comparisonWellbeingRawResult = await reportService.getWellbeingReports({
              startDay: compareToRange.startDay,
              endDay: compareToRange.endDay
            });
            // Process the raw wellbeing data to match the expected format
            const comparisonWellbeingResult = processWellbeingData(comparisonWellbeingRawResult);
            if (isMounted) {
              setComparisonWellbeingData(comparisonWellbeingResult);
            }
          } catch (error) {
            console.error('[KPIDashboard] Failed to fetch wellbeing comparison data:', error);
            if (isMounted) {
              setComparisonWellbeingData(null);
            }
          }
        }
      } catch (error) {
        console.error('[KPIDashboard] Error loading comparison data:', error);
      } finally {
        if (isMounted) {
          setLoadingComparison(false);
        }
        isLoadingRef.current = false;
        // Only mark as loaded if we completed the attempt (success or failure)
        lastCompareRangeRef.current = rangeKey;
      }
    };

    loadComparisonData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    compareToRange, // Use the full object instead of individual properties
    // Use stable boolean flags instead of data objects
    hasDauData,
    hasFeedbackData,
    hasWellbeingData,
    loading?.dau,
    loading?.feedback,
    loading?.wellbeing
  ]); // Include loading states and data existence flags to trigger comparison loading once data is ready

  // Don't render if no data is available and not loading
  if (!dauData && !feedbackData && !wellbeingData && !loading.dau && !loading.feedback && !loading.wellbeing) {
    return null;
  }

  return (
    <section className="mb-10 space-y-6">
      {loadingComparison && (
        <div className="mb-4 rounded-card border border-rm-accent-300/30 bg-rm-accent-300/10 p-3 text-sm text-rm-primary-700">
          Loading comparison data...
        </div>
      )}
      
      {/* Section 1: Employees Wellness (Full Width) */}
      {(wellbeingData || loading.wellbeing) && (
        <EmployeeWellbeing 
          data={wellbeingData}
          comparisonData={comparisonWellbeingData}
          loading={loading.wellbeing}
        />
      )}
      
      {/* Section 2 & 3: Engagement and Satisfaction (Horizontal Layout) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {(dauData || loading.dau) && (
          <EmployeeEngagement 
            dauData={dauData} 
            comparisonData={comparisonDauData}
            loading={loading.dau}
          />
        )}
        {(feedbackData || loading.feedback) && (
          <EmployeeSatisfaction 
            feedbackData={feedbackData}
            comparisonData={comparisonFeedbackData}
            loading={loading.feedback}
          />
        )}
      </div>

      {/* Section 4: Content Engagement (Full Width) */}
      {(dauData || loading.dau) && (
        <ContentEngagement
          dauData={dauData}
          comparisonData={comparisonDauData}
          loading={loading.dau}
        />
      )}
    </section>
  );
};

export default KPIDashboard;